import {
  Badge,
  Box,
  Center,
  Flex,
  HStack,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';

import { useQuery } from '@tanstack/react-query';

import { PayCalendar, payCalendarsService, Saving } from '@aslanpay/portal-common-lib';

import ProfileField from 'app/components/UI/ProfileField';
import {
  SavingStatusReasonLabels,
  SavingStatusValues,
  SavingTypeLabels,
} from 'app/constants/savings';
import useFetchSavingsPlan from 'app/hooks/useFetchSavingsPlan';

import groupPlanEntries from './groupPlanEntries';
import SavingPotImage from './SavingPotImage';
import SavingsCycleTable from './SavingsCycleTable';
import { formatSavingAmount, formatSavingDate } from './savingFormatters';

interface SavingPotDrawerProps {
  saving: Saving;
  payCalendar?: PayCalendar;
  today: string;
  onClose: () => void;
}

function SavingPotDrawer({
  saving,
  payCalendar = undefined,
  today,
  onClose,
}: SavingPotDrawerProps) {
  const isOtherCalendar = !!saving.payCalendarId && saving.payCalendarId !== payCalendar?.id;

  const { data: savingPayCalendar } = useQuery({
    queryKey: ['fetchPayCalendarForDeductionRequest', saving.payCalendarId],
    queryFn: () => payCalendarsService.fetchPayCalendar(saving.payCalendarId),
    enabled: isOtherCalendar,
  });

  const { data: plan, isLoading, isError } = useFetchSavingsPlan(saving.id);

  const calendar = isOtherCalendar ? savingPayCalendar : payCalendar;
  const currency = plan?.currency || saving.currency;
  const money = (value?: number) => formatSavingAmount(value, currency);
  const status = SavingStatusValues[saving.status];
  const name = saving.name || 'Unnamed pot';

  const renderPlan = () => {
    if (isLoading) {
      return (
        <Center py="20px">
          <Spinner color="brand.purple" />
        </Center>
      );
    }

    if (isError || !plan) {
      return (
        <Text textStyle="extraSmallRegular" color="system.error">
          Sorry, we failed to load this savings plan. Please try again.
        </Text>
      );
    }

    const groups = groupPlanEntries(plan.entries || [], calendar?.payCycles || [], today);

    return (
      <VStack align="stretch" spacing="20px">
        <SimpleGrid columns={{ base: 2, md: 5 }} spacing="16px">
          <ProfileField label="Total saved" value={money(plan.totalSaved)} />
          <ProfileField label="Points to date" value={plan.totalPointsToDate ?? '-'} />
          <ProfileField label="Projected final balance" value={money(plan.projectedFinalBalance)} />
          <ProfileField label="Projected final points" value={plan.projectedFinalPoints ?? '-'} />
          <ProfileField
            label="Projected completion"
            value={formatSavingDate(plan.projectedCompletionDate)}
          />
        </SimpleGrid>
        <SavingsCycleTable title="Past" entries={groups.past} currency={currency} />
        <SavingsCycleTable title="Current" entries={groups.current} currency={currency} />
        <SavingsCycleTable title="Future" entries={groups.future} currency={currency} />
      </VStack>
    );
  };

  return (
    <Drawer isOpen onClose={onClose} placement="right" size="xl">
      <DrawerOverlay />
      <DrawerContent maxW={{ base: '100vw', lg: 'min(1280px, 90vw)' }}>
        <DrawerCloseButton />
        <DrawerHeader pr="48px">
          <HStack spacing="10px" flexWrap="wrap">
            <Text>{name}</Text>
            <Badge colorScheme="badge.registered">{SavingTypeLabels[saving.savingType]}</Badge>
            <Badge colorScheme={`badge.${status?.color}`}>{status?.value}</Badge>
          </HStack>
        </DrawerHeader>
        <DrawerBody pb="24px">
          <VStack align="stretch" spacing="24px">
            <Flex gap="24px" flexDir={{ base: 'column', md: 'row' }}>
              <Box w={{ base: 'full', md: '240px' }} borderRadius="10px" overflow="hidden">
                <SavingPotImage imageId={saving.imageId} name={name} h="200px" />
              </Box>
              <VStack align="stretch" spacing="16px" flex="1">
                {saving.description && <Text textStyle="smallRegular">{saving.description}</Text>}
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing="16px">
                  <ProfileField label="Balance" value={money(saving.balance)} />
                  <ProfileField label="Target" value={money(saving.targetAmount)} />
                  <ProfileField label="Recurring amount" value={money(saving.recurringAmount)} />
                  <ProfileField
                    label="Cycles"
                    value={`${saving.completedPayCycles ?? 0} / ${saving.numOfPayCycles ?? '-'}`}
                  />
                  <ProfileField label="Points credited" value={saving.pointsCredited ?? 0} />
                  <ProfileField label="Points accrued" value={saving.pointsAccrued ?? 0} />
                  <ProfileField label="Created" value={formatSavingDate(saving.createdDate)} />
                  <ProfileField label="Started" value={formatSavingDate(saving.startDate)} />
                  <ProfileField
                    label={saving.endedDate ? 'Ended' : 'Projected end'}
                    value={formatSavingDate(saving.endedDate || saving.projectedEndDate)}
                  />
                  <ProfileField
                    label="Status reason"
                    value={
                      saving.statusReasonType
                        ? SavingStatusReasonLabels[saving.statusReasonType]
                        : '-'
                    }
                  />
                  <ProfileField label="Pot ID" value={saving.potId} copyToClipboard />
                  <ProfileField label="Savings ID" value={saving.id} copyToClipboard />
                </SimpleGrid>
              </VStack>
            </Flex>
            {renderPlan()}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export default SavingPotDrawer;
